import { type ReactNode } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useRoute } from '../../contexts/RouteContext';
import Button from '../ui/Button';
import reslocateLogo from '../../img/reslocate_logo.svg';

interface AppShellProps {
  children: ReactNode;
}

/**
 * Application shell providing the shared layout for the Partner Onboarding
 * experience. Includes a top header with the Reslocate brand on the left and
 * a Sign Out control on the right when an authenticated partner access
 * session is active. The onboarding form content is rendered in the main
 * region.
 */
export default function AppShell({ children }: AppShellProps) {
  const { status, signOut } = useAuth();
  const { navigate } = useRoute();

  const handleSignOut = async () => {
    // M4-R21F: Sign out clears the Supabase Auth session. The portal is gated
    // on the Supabase Auth session (App.tsx), so this returns the user to the
    // landing page. The partner context derives from the authenticated
    // membership, so no separate custom session needs clearing.
    await signOut();
    navigate('/');
  };

  return (
    <div className="flex min-h-dvh flex-col bg-surface">
      <header className="border-b border-border bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2">
            <img
              src={reslocateLogo}
              alt="Reslocate"
              className="h-8 w-8"
            />
            <span className="text-lg font-semibold text-brand-blue">
              Reslocate
            </span>
            <span className="hidden text-sm text-text-muted sm:inline">
              Partner Onboarding
            </span>
          </div>
          {status === 'authenticated' ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              aria-label="Sign out"
            >
              Sign out
            </Button>
          ) : null}
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-border bg-white">
        <div className="mx-auto max-w-7xl px-4 py-4 text-center text-xs text-text-muted sm:px-6">
          © {new Date().getFullYear()} Reslocate. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
